import React, { createContext, useContext, useEffect, useMemo } from 'react';

export type TimeToMountType =
  | 'page-ready'
  | 'content-loading'
  | 'initial-page-load';

export type TrackingData = {
  [key: string]: string | number | void;
};

export type TimeToMountData = {
  timeToMountType?: TimeToMountType;
  trackingData?: TrackingData;
};

type ContextProps = {
  recordTimeToMount: (
    timeToMountType: TimeToMountType,
    trackingData: TrackingData
  ) => void;
};

const defaultContextValue = {
  recordTimeToMount(
    timeToMountType: TimeToMountType,
    trackingData: TrackingData
  ) {
    console.info(`Would have tracked ${timeToMountType}`, trackingData);
  },
};

const TimeToMountContext = createContext<ContextProps>(defaultContextValue);

type ProviderProps = ContextProps & {
  children: React.ReactNode;
};

export function RecordTimeToMountProvider({
  children,
  recordTimeToMount,
}: ProviderProps) {
  const contextValue = useMemo(() => ({ recordTimeToMount }), [
    recordTimeToMount,
  ]);
  return (
    <TimeToMountContext.Provider value={contextValue}>
      {children}
    </TimeToMountContext.Provider>
  );
}

type Props = {
  children?: React.ReactNode;
  /** What kind of mount is being tracked here? */
  timeToMountType: TimeToMountType;
  /**
   * To track what effectively are updates, not mounts,
   * set a value that changes between updates to track
   */
  timeToMountId?: string;
  /** Tracking data is every other prop */
  [key: string]: string | number | any;
};

export function RecordTimeToMount({
  children,
  timeToMountType,
  timeToMountId,
  ...trackingData
}: Props) {
  const { recordTimeToMount } = useContext(TimeToMountContext);
  const onMount = () => recordTimeToMount(timeToMountType, trackingData);
  useEffect(onMount, [timeToMountId]);
  return children || null;
}
